import React, { useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { Card, Col, Row, Space, Switch, Typography } from "@mimber/ui-web"
import { InputNumberField, SelectField, DatePickerField } from "@mimber/forms"
import { RecurrenceInterval } from "@mimber/schema"
import type { RecurrenceConfig } from "@mimber/schema"
import { ScheduledActionCalendarPreview } from "@/components/blocks/ScheduledActionCalendarPreview"
import {
  INTERVAL_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  WEEK_INDEX_OPTIONS,
  MONTH_OPTIONS,
} from "./RecurrenceConfigForm.utils"
import type { RecurrenceConfigFormProps } from "./RecurrenceConfigForm.types"

const { Text } = Typography

export const RecurrenceConfigForm: React.FunctionComponent<
  RecurrenceConfigFormProps
> = ({ disabled = false, startDate }) => {
  const { control, setValue } = useFormContext<{
    recurrence?: RecurrenceConfig
  }>()

  const recurrence = useWatch({ control, name: "recurrence" })
  const [enabled, setEnabled] = useState(!!recurrence)

  const intervalType = recurrence?.intervalType
  const isWeekly = intervalType === RecurrenceInterval.WEEKLY
  const isMonthly = intervalType === RecurrenceInterval.MONTHLY
  const isYearly = intervalType === RecurrenceInterval.YEARLY

  const handleToggle = (checked: boolean) => {
    setEnabled(checked)
    if (!checked) {
      setValue("recurrence", undefined)
    }
  }

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Switch
          checked={enabled}
          onChange={handleToggle}
          disabled={disabled}
          data-testid="recurrence-toggle"
        />
        <Text>Recurring Event</Text>
      </Space>

      {enabled && (
        <>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <InputNumberField
                name="recurrence.intervalLength"
                label="Every"
                min={1}
                required
                disabled={disabled}
              />
            </Col>
            <Col xs={24} sm={12}>
              <SelectField
                name="recurrence.intervalType"
                label="Interval"
                options={INTERVAL_OPTIONS}
                required
                disabled={disabled}
              />
            </Col>
          </Row>

          {isWeekly && (
            <SelectField
              name="recurrence.daysOfWeek"
              label="On these days"
              options={DAY_OF_WEEK_OPTIONS}
              mode="multiple"
              disabled={disabled}
            />
          )}

          {isMonthly && (
            <Row gutter={16} data-testid="monthly-section">
              <Col xs={24} sm={12}>
                <InputNumberField
                  name="recurrence.dayOfMonth"
                  label="On day"
                  min={1}
                  max={31}
                  disabled={disabled}
                />
              </Col>
              <Col xs={24} sm={12}>
                <SelectField
                  name="recurrence.weekIndex"
                  label="Week of month"
                  options={WEEK_INDEX_OPTIONS}
                  disabled={disabled}
                />
              </Col>
            </Row>
          )}

          {isYearly && (
            <Row gutter={16} data-testid="yearly-section">
              <Col xs={24} sm={12}>
                <SelectField
                  name="recurrence.monthOfYear"
                  label="Month"
                  options={MONTH_OPTIONS}
                  disabled={disabled}
                />
              </Col>
              <Col xs={24} sm={12}>
                <InputNumberField
                  name="recurrence.dayOfMonth"
                  label="On day"
                  min={1}
                  max={31}
                  disabled={disabled}
                />
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <DatePickerField
                name="recurrence.endsAt"
                label="Ends At"
                showTime
                disabled={disabled}
              />
            </Col>
            <Col xs={24} sm={12}>
              <InputNumberField
                name="recurrence.maxOccurrences"
                label="Max Occurrences"
                min={1}
                disabled={disabled}
              />
            </Col>
          </Row>

          {startDate && (
            <Row>
              <Col xs={24}>
                <Card size="small" title="Preview" style={{ marginTop: 16 }}>
                  <ScheduledActionCalendarPreview
                    scheduledFor={startDate}
                    isRecurring={enabled}
                    intervalLength={recurrence?.intervalLength}
                    intervalType={recurrence?.intervalType}
                    endsAt={recurrence?.endsAt ?? undefined}
                    maxOccurrences={recurrence?.maxOccurrences ?? undefined}
                    daysOfWeek={recurrence?.daysOfWeek ?? undefined}
                    dayOfMonth={recurrence?.dayOfMonth ?? undefined}
                    weekIndex={recurrence?.weekIndex ?? undefined}
                    monthOfYear={recurrence?.monthOfYear ?? undefined}
                  />
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  )
}
